import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import { toast } from "react-toastify";

import SelectField from "../ui/SelectField";

const ModalFullHV = ({ onClose, onGenerate }) => {
  const [clientes, setClientes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [sedeId, setSedeId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [equipoId, setEquipoId] = useState("");
  const [formato, setFormato] = useState("estandar");

  const API_URL = process.env.REACT_APP_API_URL;

  // -------------------------------------------
  // Normalizador universal
  // -------------------------------------------
  const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.clientes)) return data.clientes;
    if (Array.isArray(data?.sedes)) return data.sedes;
    if (Array.isArray(data?.areas)) return data.areas;
    if (Array.isArray(data?.equipos)) return data.equipos;
    return [];
  };

  // -------------------------------------------
  // Loaders
  // -------------------------------------------
  useEffect(() => {
    loadClientes();
    // eslint-disable-next-line
  }, []);

  const loadClientes = async () => {
    try {
      const res = await fetch(`${API_URL}/clientes`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      setClientes(toArray(data));
    } catch {
      toast.error("Error cargando clientes");
    }
  };

  const loadSedes = async (id) => {
    try {
      const res = await fetch(`${API_URL}/sedes/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      setSedes(toArray(data));
    } catch {
      toast.error("Error cargando sedes");
    }
  };

  const loadAreas = async (id) => {
    try {
      const res = await fetch(`${API_URL}/areas/bySede/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      setAreas(toArray(data));
    } catch {
      toast.error("Error cargando áreas");
    }
  };

  const loadEquipos = async (id) => {
    try {
      const res = await fetch(`${API_URL}/equipos/byArea/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      setEquipos(toArray(data));
    } catch {
      toast.error("Error cargando equipos");
    }
  };

  // -------------------------------------------
  // Handler compatible con SelectField
  // -------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "cliente") {
      setClienteId(value);
      setSedeId("");
      setAreaId("");
      setEquipoId("");
      if (value) loadSedes(value);
      return;
    }

    if (name === "sede") {
      setSedeId(value);
      setAreaId("");
      setEquipoId("");
      if (value) loadAreas(value);
      return;
    }

    if (name === "area") {
      setAreaId(value);
      setEquipoId("");
      if (value) loadEquipos(value);
      return;
    }

    if (name === "equipo") setEquipoId(value);
  };

  const handleGenerate = () => {
    setIsLoading(true);
    if (!clienteId || !sedeId || !areaId) {
      setIsLoading(false);
      return toast.warning("Selecciona cliente, sede y área.");
    }

    onGenerate({
      clienteId,
      sedeId,
      areaId,
      equipoId: equipoId || null,
      formato,
    });

    setIsLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-2xl max-h-[95vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative px-6 py-4 border-b">
          <h2 className="text-xl font-semibold">Seleccionar equipo o área</h2>
          <button
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scroll interno */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8 space-y-6">
            <SelectField
              label="Cliente"
              name="cliente"
              value={clienteId}
              options={clientes}
              onChange={handleChange}
              displayFn={(c) => c.name}
            />

            {clienteId && (
              <SelectField
                label="Sede"
                name="sede"
                value={sedeId}
                options={sedes}
                onChange={handleChange}
              />
            )}

            {sedeId && (
              <SelectField
                label="Área"
                name="area"
                value={areaId}
                options={areas}
                onChange={handleChange}
                displayFn={(a) =>
                  a.grupo ? `${a.grupo.name} - ${a.name}` : a.name
                }
              />
            )}

            {areaId && (
              <SelectField
                label="Equipo (opcional)"
                name="equipo"
                value={equipoId}
                options={equipos}
                onChange={handleChange}
                displayFn={(eq) =>
                  `[${eq.categoria?.name}] ${eq.marca?.name} ${eq.modelo?.name} - ${eq.serial}`
                }
              />
            )}

            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Formato de hoja de vida
              </p>
              <div className="grid grid-cols-2 gap-3">
                <label
                  className={`flex flex-col gap-1 border rounded-lg p-3 cursor-pointer transition-colors ${
                    formato === "estandar"
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="formato"
                    value="estandar"
                    checked={formato === "estandar"}
                    onChange={(e) => setFormato(e.target.value)}
                    className="sr-only"
                  />
                  <span className="font-medium text-sm text-gray-800">
                    Estándar del sistema
                  </span>
                  <span className="text-xs text-gray-500">
                    Formato Previonix en carta vertical
                  </span>
                </label>
                <label
                  className={`flex flex-col gap-1 border rounded-lg p-3 cursor-pointer transition-colors ${
                    formato === "minsalud"
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="formato"
                    value="minsalud"
                    checked={formato === "minsalud"}
                    onChange={(e) => setFormato(e.target.value)}
                    className="sr-only"
                  />
                  <span className="font-medium text-sm text-gray-800">
                    Formato MinSalud
                  </span>
                  <span className="text-xs text-gray-500">
                    Hoja de vida biomédica — carta horizontal
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t space-y-2">
          <p className="text-xs text-gray-500">
            El ZIP estará disponible para descargar durante 24 horas.
          </p>
          <Button className="w-full" onClick={handleGenerate}>
            {isLoading ? "Enviando solicitud..." : "Solicitar generación"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalFullHV;
